/* eslint-disable react/jsx-no-undef */
import React, { useState, useEffect } from 'react';
import {
  Users, UserCheck, UserX, TrendingUp, RefreshCw, AlertCircle,
  CheckCircle, Search, UserCircle, MoreHorizontal, Edit, Trash2,
  ToggleLeft, ToggleRight, Eye, Mail, Phone, Shield, AlertTriangle,
  Crown, X, Key, Copy, Calendar, ShieldOff,
} from 'lucide-react';
import UsersService from './home/homeservice';
import { useAuth } from './hooks/AuthContext';

// ─── Design tokens ────────────────────────────────────────────────────────────
const NAV  = '#001f3f';
const TEAL = '#0d9488';

// ─── Shared atoms ─────────────────────────────────────────────────────────────
const Badge = ({ children, color = 'slate' }) => {
  const map = {
    green:  'bg-emerald-100 text-emerald-700 border border-emerald-200',
    red:    'bg-red-100 text-red-700 border border-red-200',
    amber:  'bg-amber-100 text-amber-700 border border-amber-200',
    blue:   'bg-blue-100 text-blue-700 border border-blue-200',
    purple: 'bg-purple-100 text-purple-700 border border-purple-200',
    slate:  'bg-slate-100 text-slate-600 border border-slate-200',
    teal:   'bg-teal-100 text-teal-700 border border-teal-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[color]}`}>
      {children}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: accent + '18' }}>
      <Icon size={20} style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-slate-800 leading-tight">{value}</p>
    </div>
  </div>
);

const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
      {children}
    </div>
  </div>
);

const ModalHeader = ({ icon: Icon, iconColor = TEAL, title, onClose }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: iconColor + '18' }}>
        <Icon size={16} style={{ color: iconColor }} />
      </div>
      <h3 className="text-base font-bold text-slate-800">{title}</h3>
    </div>
    {onClose && (
      <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors">
        <X size={16} />
      </button>
    )}
  </div>
);

const Btn = ({ children, onClick, disabled, variant = 'primary', size = 'md', loading }) => {
  const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm', lg: 'px-5 py-2.5 text-sm' };
  const styles = {
    primary: { background: `linear-gradient(135deg, ${NAV}, ${TEAL})`, color: '#fff' },
    danger:  { backgroundColor: '#f43f5e', color: '#fff' },
    warning: { backgroundColor: '#f59e0b', color: '#fff' },
    success: { backgroundColor: '#10b981', color: '#fff' },
    outline: {},
  };
  const cls = {
    primary: 'hover:opacity-90',
    danger:  'hover:opacity-90',
    warning: 'hover:opacity-90',
    success: 'hover:opacity-90',
    outline: 'border border-slate-200 text-slate-700 hover:bg-slate-50',
  };
  return (
    <button onClick={onClick} disabled={disabled || loading}
      className={`inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-50 ${sizes[size]} ${cls[variant]}`}
      style={styles[variant]}>
      {loading && <RefreshCw size={13} className="animate-spin" />}
      {children}
    </button>
  );
};

// ─── Role helpers ─────────────────────────────────────────────────────────────
const getRoleDisplay = (user) => user.is_superuser ? 'Super Admin' : user.is_staff ? 'Admin' : 'User';
const getRoleColor   = (user) => user.is_superuser ? 'purple' : user.is_staff ? 'blue' : 'slate';

// ── Engagement grading ───────────────────────────────────────────────────────
// The server grades each row from last_login and sorts and counts across the
// whole list. These definitions mirror it so the unpaginated fallback path can
// still show a grade, and so the pills have their colours to hand.
const GRADES = [
  { key:'frequent', label:'Frequent', color:'#1B8E3E', priority:1 },
  { key:'often',    label:'Often',    color:'#8FD694', priority:2 },
  { key:'mostly',   label:'Mostly',   color:'#9AA0A6', priority:3 },
  { key:'rarely',   label:'Rarely',   color:'#F28C28', priority:4 },
  { key:'never',    label:'Never',    color:'#D93025', priority:5 },
];
const GRADE_BY_KEY = Object.fromEntries(GRADES.map(g => [g.key, g]));
const DEFAULT_THRESHOLDS = { frequent_days:2, often_days:7, mostly_days:30 };

const gradeOf = (user, limits = DEFAULT_THRESHOLDS) => {
  if (user?.engagement?.key) return { ...GRADE_BY_KEY[user.engagement.key], ...user.engagement };
  if (!user?.last_login) return { ...GRADE_BY_KEY.never, days_since_login:null };
  const days = Math.max(Math.floor((Date.now() - new Date(user.last_login)) / 86400000), 0);
  const key = days <= limits.frequent_days ? 'frequent'
    : days <= limits.often_days ? 'often'
    : days <= limits.mostly_days ? 'mostly'
    : 'rarely';
  return { ...GRADE_BY_KEY[key], days_since_login:days };
};

// White text on the pale green reads badly, so that one grade takes dark ink.
const pillInk = (key) => (key === 'often' ? '#0B3D1E' : '#FFFFFF');

const gradeTitle = (grade) => {
  if (grade.days_since_login === null || grade.days_since_login === undefined) {
    return `${grade.label} — has never logged in`;
  }
  if (grade.days_since_login === 0) return `${grade.label} — logged in today`;
  const d = grade.days_since_login;
  return `${grade.label} — last logged in ${d} day${d === 1 ? '' : 's'} ago`;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }) : 'Never';

// ══════════════════════════════════════════════════════════════════════════════
const UserManagement = () => {
  const authContext = useAuth();
  const { user: currentUser, isLoggedIn, hasPermission = () => true } = authContext || {};

  const [users,         setUsers]         = useState([]);
  const [stats,         setStats]         = useState({});
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error,         setError]         = useState(null);
  const [success,       setSuccess]       = useState('');

  const [searchTerm,   setSearchTerm]   = useState('');
  const [gradeFilter,  setGradeFilter]  = useState('');
  const [gradeCounts,  setGradeCounts]  = useState([]);
  const [thresholds,   setThresholds]   = useState(DEFAULT_THRESHOLDS);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize]                       = useState(10);
  const [totalPages,   setTotalPages]   = useState(0);
  const [totalUsers,   setTotalUsers]   = useState(0);

  const [showDropdown,            setShowDropdown]            = useState(null);
  const [deleteModal,             setDeleteModal]             = useState(null);
  const [roleModal,               setRoleModal]               = useState(null);
  const [pwdResetConfirmModal,    setPwdResetConfirmModal]    = useState(null);
  const [pwdResetResult,          setPwdResetResult]          = useState(null);
  const [availableRoles,          setAvailableRoles]          = useState([]);

  // ── Permissions ──────────────────────────────────────────────────────────────
  const canManageUsers  = () => currentUser && (currentUser.is_staff || currentUser.is_admin || currentUser.is_superuser);
  const canDeleteUsers  = () => currentUser && (currentUser.is_superuser === true || currentUser.is_staff === true);
  const canManageRoles  = () => currentUser?.is_superuser === true;

  // ── Messages ──────────────────────────────────────────────────────────────────
  const showError   = (msg) => { setError(msg);   setTimeout(() => setError(null),   4000); };
  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  // ── Data fetchers ─────────────────────────────────────────────────────────────
  const fetchUsers = async (page = currentPage, search = searchTerm, grade = gradeFilter) => {
    try {
      setLoading(true);
      setError(null);
      if (canDeleteUsers()) {
        try {
          const r = await UsersService.getUsersWithPagination(page, pageSize, search, grade);
          setUsers(r.users || r.results || r);
          setTotalPages(r.total_pages || Math.ceil((r.total || r.count || 0) / pageSize));
          setTotalUsers(r.total || r.count || 0);
          if (r.grade_counts) setGradeCounts(r.grade_counts);
          if (r.engagement_thresholds) setThresholds(r.engagement_thresholds);
          setCurrentPage(page);
          return;
        } catch {}
      }
      // Fallback: the whole list at once. Grade, count, filter and sort here so
      // the screen behaves the same way when the paginated endpoint is refused.
      let all = await UsersService.getAllUsers();
      if (search) all = all.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.nida?.toLowerCase().includes(search.toLowerCase())
      );

      const counted = GRADES.map(g => ({
        ...g, count: all.filter(u => gradeOf(u, thresholds).key === g.key).length,
      }));
      setGradeCounts(counted);

      if (grade) all = all.filter(u => gradeOf(u, thresholds).key === grade);
      all = [...all].sort((a, b) => {
        const ga = gradeOf(a, thresholds), gb = gradeOf(b, thresholds);
        if (ga.priority !== gb.priority) return ga.priority - gb.priority;
        if (ga.key === 'never') {
          return new Date(a.date_joined || 0) - new Date(b.date_joined || 0);
        }
        return new Date(b.last_login || 0) - new Date(a.last_login || 0);
      });

      const start = (page - 1) * pageSize;
      setUsers(all.slice(start, start + pageSize));
      setTotalPages(Math.ceil(all.length / pageSize));
      setTotalUsers(all.length);
      setCurrentPage(page);
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Failed to load users.');
    } finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      if (canDeleteUsers()) {
        try { const d = await UsersService.getUserStats(); setStats(d); return; } catch {}
      }
      const active = users.filter(u => u.is_active).length;
      setStats({ total_users:users.length, active_users:active, inactive_users:users.length-active, activity_rate:users.length?Math.round((active/users.length)*100):0 });
    } catch {}
  };

  const fetchAvailableRoles = async () => {
    try {
      if (canManageRoles()) {
        try { const r = await UsersService.getAvailableRoles(); setAvailableRoles(r.available_roles||[]); return; } catch {}
      }
      setAvailableRoles([
        { key:'regular',   display:'Regular User',         description:'Standard user with basic permissions' },
        { key:'staff',     display:'Administrator',         description:'Can manage users and access admin features' },
        { key:'superuser', display:'Super Administrator',   description:'Full access to all system features' },
      ]);
    } catch {}
  };

  const refreshData = () => { fetchUsers(currentPage, searchTerm, gradeFilter); fetchStats(); if (canManageRoles()) fetchAvailableRoles(); };

  useEffect(() => {
    if (isLoggedIn) { fetchUsers(); if (canManageRoles()) fetchAvailableRoles(); }
  }, [isLoggedIn]);

  useEffect(() => { fetchStats(); }, [users]);

  // ── Actions ───────────────────────────────────────────────────────────────────
  const handleToggleStatus = async (userId, userName) => {
    if (!canDeleteUsers()) { showError('No permission to toggle status.'); return; }
    if (!window.confirm(`Toggle ${userName}'s status?`)) return;
    try {
      setActionLoading(userId);
      const r = await UsersService.toggleUserStatus(userId);
      showSuccess(r.message || `${userName}'s status updated`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !u.is_active } : u));
      fetchStats();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to toggle status');
    } finally { setActionLoading(null); setShowDropdown(null); }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    const { userId, userName } = deleteModal;
    try {
      setActionLoading(userId);
      const r = await UsersService.deleteUser(userId);
      showSuccess(r.message || `${userName} deleted`);
      refreshData();
    } catch (err) {
      showError(err.response?.data?.error || `Failed to delete ${userName}`);
    } finally { setActionLoading(null); setShowDropdown(null); setDeleteModal(null); }
  };

  const confirmResetPassword = async () => {
    if (!pwdResetConfirmModal) return;
    const { userId, userName } = pwdResetConfirmModal;
    try {
      setActionLoading(userId);
      const r = await UsersService.resetUserPassword(userId);
      setPwdResetResult({ userName, defaultPassword: r.default_password, userId });
      showSuccess(`Password reset for ${userName}`);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to reset password');
    } finally { setActionLoading(null); setPwdResetConfirmModal(null); }
  };

  const updateRole = async (roleType) => {
    if (!roleModal) return;
    if (currentUser?.id === roleModal.id && roleType !== 'superuser') {
      showError('Cannot change your own role.'); return;
    }
    try {
      setActionLoading(roleModal.id);
      const r = await UsersService.updateUserRoles(roleModal.id, { role_type: roleType });
      showSuccess(r.message || `Role updated for ${roleModal.name || roleModal.email}`);
      setUsers(prev => prev.map(u => u.id === roleModal.id
        ? { ...u, is_staff: roleType === 'staff' || roleType === 'superuser', is_superuser: roleType === 'superuser' }
        : u
      ));
      setRoleModal(null); fetchStats();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to update role');
    } finally { setActionLoading(null); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => showSuccess('Copied!')).catch(() => showError('Copy failed'));
  };

  // ── Dropdown close on outside click ──────────────────────────────────────────
  useEffect(() => {
    const handler = () => setShowDropdown(null);
    if (showDropdown) { document.addEventListener('click', handler); return () => document.removeEventListener('click', handler); }
  }, [showDropdown]);

  // ── Not logged in ─────────────────────────────────────────────────────────────
  if (!isLoggedIn) return (
    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
        <AlertCircle size={32} className="text-red-400" />
      </div>
      <h3 className="text-base font-bold text-slate-800 mb-2">Authentication Required</h3>
      <p className="text-sm text-slate-500">Please log in to access user management.</p>
    </div>
  );

  if (loading && users.length === 0) return (
    <div className="bg-white rounded-xl border border-slate-200 p-12 flex items-center justify-center">
      <div className="flex items-center gap-3" style={{ color: TEAL }}>
        <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: `${TEAL} transparent transparent transparent` }} />
        <span className="text-sm font-medium">Loading users...</span>
      </div>
    </div>
  );

  const currentYear  = new Date().getFullYear();
  const defaultPwd   = `JAMBO${currentYear}`;

  return (
    <div className="space-y-5 p-10">

      {/* ── Toast messages ──────────────────────────────────────────────── */}
      {success && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm">
          <CheckCircle size={15} className="flex-shrink-0" /><span>{success}</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          <AlertCircle size={15} className="flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X size={13} /></button>
        </div>
      )}

      {/* ── Admin notice ─────────────────────────────────────────────────── */}
      {canDeleteUsers() && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl text-sm">
          <Shield size={15} className="flex-shrink-0" />
          <span>Administrator view — you can manage users{canManageRoles() && ', roles,'} and permissions.</span>
        </div>
      )}

      {/* ── Stat cards ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users}     label="Total Users"    value={totalUsers}                  accent={NAV} />
        <StatCard icon={UserCheck} label="Active Users"   value={stats.active_users   || 0}   accent="#10b981" />
        <StatCard icon={UserX}     label="Inactive Users" value={stats.inactive_users || 0}   accent="#f43f5e" />
        <StatCard icon={TrendingUp} label="Activity Rate" value={`${stats.activity_rate || 0}%`} accent="#8b5cf6" />
      </div>

      {/* ── Main panel ───────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">

        {/* Panel header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-slate-800">User Management</h1>
            <p className="text-xs text-slate-400 mt-0.5">{totalUsers} total users{canDeleteUsers() && ' · Administrator view'}</p>
          </div>
          <Btn onClick={refreshData} disabled={loading} loading={loading} variant="primary" size="sm">
            <RefreshCw size={13} /> Refresh
          </Btn>
        </div>

        {/* Search bar */}
        <div className="px-6 py-3 border-b border-slate-100">
          <div className="relative max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder="Search users..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); fetchUsers(1, e.target.value, gradeFilter); }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" />
          </div>
        </div>

        {/* Engagement: how many people sit in each grade, and a filter. The
            counts always describe the whole list, so they can be read as a
            conversion scoreboard even while a filter is applied. */}
        <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mr-1">
            Engagement
          </span>

          <button
            onClick={() => { setGradeFilter(''); setCurrentPage(1); fetchUsers(1, searchTerm, ''); }}
            className={`text-[11px] font-semibold rounded-full px-3 py-1 border transition-colors ${
              gradeFilter === '' ? 'text-white border-transparent' : 'text-slate-600 bg-white border-slate-200 hover:bg-slate-50'}`}
            style={gradeFilter === '' ? { backgroundColor: NAV } : {}}
          >
            All {gradeCounts.length ? gradeCounts.reduce((sum, g) => sum + g.count, 0) : totalUsers}
          </button>

          {(gradeCounts.length ? gradeCounts : GRADES.map(g => ({ ...g, count: 0 }))).map(grade => {
            const active = gradeFilter === grade.key;
            return (
              <button
                key={grade.key}
                onClick={() => {
                  const next = active ? '' : grade.key;
                  setGradeFilter(next); setCurrentPage(1); fetchUsers(1, searchTerm, next);
                }}
                title={`${grade.label}: ${grade.count} ${grade.count === 1 ? 'user' : 'users'}`}
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1 border transition-colors ${
                  active ? 'border-transparent' : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-600'}`}
                style={active ? { backgroundColor: grade.color, color: pillInk(grade.key) } : {}}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: grade.color }} />
                {grade.label} {grade.count}
              </button>
            );
          })}

          <span className="ml-auto text-[11px] text-slate-400">
            Graded on last login · Frequent ≤ {thresholds.frequent_days}d · Often ≤ {thresholds.often_days}d · Mostly ≤ {thresholds.mostly_days}d
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {['User','Contact','NIDA','Role','Status','Joined','Last Login','Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {users.length > 0 ? users.map(user => (
                <tr key={user.id} className="hover:bg-slate-50/60 transition-colors">

                  {/* User */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: NAV + '18' }}>
                        {user.avatar
                          ? <img src={user.avatar} className="w-9 h-9 rounded-xl object-cover" alt="" />
                          : <span className="text-sm font-bold" style={{ color: NAV }}>{(user.name||'?')[0].toUpperCase()}</span>
                        }
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {user.name || 'No Name'}
                          {currentUser?.id === user.id && (
                            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded-full font-bold">You</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-5 py-3.5">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <Phone size={11} className="text-slate-400" />{user.phone || <span className="text-slate-300">No phone</span>}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <Mail size={11} className="text-slate-400" /><span className="truncate max-w-[140px]">{user.email}</span>
                      </div>
                    </div>
                  </td>

                  {/* NIDA — carries the engagement grade as a coloured pill. The
                      number sits inside it when there is one, and the grade is
                      always spelled out, so colour is never the only signal. */}
                  <td className="px-5 py-3.5">
                    {(() => {
                      const grade = gradeOf(user, thresholds);
                      return (
                        <span
                          title={gradeTitle(grade)}
                          aria-label={`${user.nida || 'No NIDA'} · ${gradeTitle(grade)}`}
                          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold font-mono"
                          style={{ backgroundColor: grade.color, color: pillInk(grade.key) }}
                        >
                          {user.nida ? user.nida : grade.label}
                          {user.nida ? (
                            <span className="font-sans font-medium opacity-90">· {grade.label}</span>
                          ) : null}
                        </span>
                      );
                    })()}
                  </td>

                  {/* Role */}
                  <td className="px-5 py-3.5">
                    <Badge color={getRoleColor(user)}>
                      {user.is_superuser ? <Crown size={10}/> : <Shield size={10}/>}
                      {getRoleDisplay(user)}
                    </Badge>
                  </td>

                  {/* Status */}
                  <td className="px-5 py-3.5">
                    {!user.is_active
                      ? <Badge color="red">Inactive</Badge>
                      : !user.is_verified
                        ? <Badge color="amber">Unverified</Badge>
                        : <Badge color="green">Active</Badge>
                    }
                  </td>

                  {/* Joined */}
                  <td className="px-5 py-3.5">
                    <div className="text-xs text-slate-600 flex items-center gap-1.5">
                      <Calendar size={11} className="text-slate-400" />{fmtDate(user.date_joined)}
                    </div>
                    {user.account_age_days !== undefined && <p className="text-[10px] text-slate-400 mt-0.5">{user.account_age_days}d ago</p>}
                  </td>

                  {/* Last Login */}
                  <td className="px-5 py-3.5 text-xs text-slate-500">{user.last_login_formatted || fmtDateTime(user.last_login)}</td>

                  {/* Actions */}
                  <td className="px-5 py-3.5 relative">
                    <button onClick={e => { e.stopPropagation(); setShowDropdown(showDropdown === user.id ? null : user.id); }}
                      disabled={actionLoading === user.id}
                      className="p-1.5 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded-lg transition-colors disabled:opacity-50">
                      {actionLoading === user.id
                        ? <RefreshCw size={15} className="animate-spin" />
                        : <MoreHorizontal size={15} />
                      }
                    </button>

                    {showDropdown === user.id && (
                      <div className="absolute right-4 top-full mt-1 z-20 w-48 bg-white rounded-xl border border-slate-200 shadow-lg py-1 overflow-hidden">
                        <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 transition-colors">
                          <Eye size={14} className="text-slate-400" /> View Details
                        </button>
                        <button className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 transition-colors">
                          <Edit size={14} className="text-slate-400" /> Edit User
                        </button>
                        {canManageRoles() && (
                          <button onClick={() => { setRoleModal(user); setShowDropdown(null); }}
                            className="w-full text-left px-4 py-2.5 text-sm text-purple-600 hover:bg-slate-50 flex items-center gap-2.5 transition-colors">
                            <Crown size={14} /> Manage Role
                          </button>
                        )}
                        {canDeleteUsers() && (
                          <button onClick={() => handleToggleStatus(user.id, user.name)}
                            className="w-full text-left px-4 py-2.5 text-sm text-blue-600 hover:bg-slate-50 flex items-center gap-2.5 transition-colors">
                            {user.is_active ? <><ToggleLeft size={14}/> Deactivate</> : <><ToggleRight size={14}/> Activate</>}
                          </button>
                        )}
                        {canDeleteUsers() && (
                          <button onClick={() => { setPwdResetConfirmModal({ userId: user.id, userName: user.name }); setShowDropdown(null); }}
                            className="w-full text-left px-4 py-2.5 text-sm text-amber-600 hover:bg-slate-50 flex items-center gap-2.5 transition-colors">
                            <Key size={14} /> Reset Password
                          </button>
                        )}
                        {canDeleteUsers() && currentUser?.id !== user.id && (
                          <>
                            <div className="my-1 border-t border-slate-100" />
                            <button onClick={() => { setDeleteModal({ userId: user.id, userName: user.name }); setShowDropdown(null); }}
                              className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-slate-50 flex items-center gap-2.5 transition-colors">
                              <Trash2 size={14} /> Delete User
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="px-6 py-16 text-center text-slate-400">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                      <UserCircle size={24} className="text-slate-300" />
                    </div>
                    <p className="text-sm font-medium text-slate-600 mb-1">No users found</p>
                    <p className="text-xs">{searchTerm ? 'Try adjusting your search.' : 'No users in the system yet.'}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">Showing {users.length} of {totalUsers} users</p>
            <div className="flex items-center gap-1">
              <button onClick={() => fetchUsers(currentPage - 1, searchTerm, gradeFilter)} disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                Prev
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (page > totalPages) return null;
                return (
                  <button key={page} onClick={() => fetchUsers(page, searchTerm, gradeFilter)}
                    className={`w-8 h-8 text-sm rounded-xl font-medium transition-colors ${
                      currentPage === page ? 'text-white' : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                    style={currentPage === page ? { backgroundColor: NAV } : {}}>
                    {page}
                  </button>
                );
              })}
              <button onClick={() => fetchUsers(currentPage + 1, searchTerm, gradeFilter)} disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ══ MODALS ════════════════════════════════════════════════════════ */}

      {/* Delete confirm */}
      {deleteModal && (
        <Modal>
          <ModalHeader icon={AlertTriangle} iconColor="#f43f5e" title="Delete User" onClose={() => setDeleteModal(null)} />
          <div className="px-6 py-5">
            <p className="text-sm text-slate-600 mb-4">
              Are you sure you want to delete <strong>{deleteModal.userName}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Btn variant="outline" onClick={() => setDeleteModal(null)} disabled={actionLoading === deleteModal.userId}>Cancel</Btn>
              <Btn variant="danger" onClick={confirmDelete} loading={actionLoading === deleteModal.userId}>
                <Trash2 size={13} /> Delete
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Password reset confirm */}
      {pwdResetConfirmModal && (
        <Modal>
          <ModalHeader icon={Key} iconColor="#f59e0b" title="Confirm Password Reset" onClose={() => setPwdResetConfirmModal(null)} />
          <div className="px-6 py-5">
            <p className="text-sm text-slate-600 mb-4">
              Reset password for <strong>{pwdResetConfirmModal.userName}</strong>?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
              <p className="text-xs text-amber-700 font-semibold mb-1">Password will be reset to:</p>
              <code className="text-base font-bold text-amber-800 bg-amber-100 px-3 py-1.5 rounded-lg block">{defaultPwd}</code>
              <p className="text-[10px] text-amber-600 mt-2">Share this securely with the user.</p>
            </div>
            <div className="flex justify-end gap-3">
              <Btn variant="outline" onClick={() => setPwdResetConfirmModal(null)} disabled={actionLoading === pwdResetConfirmModal.userId}>Cancel</Btn>
              <Btn variant="warning" onClick={confirmResetPassword} loading={actionLoading === pwdResetConfirmModal.userId}>
                <Key size={13} /> Reset Password
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Password reset result */}
      {pwdResetResult && (
        <Modal>
          <ModalHeader icon={Key} iconColor="#10b981" title="Password Reset Successful" onClose={() => setPwdResetResult(null)} />
          <div className="px-6 py-5">
            <p className="text-sm text-slate-600 mb-4">
              New password for <strong>{pwdResetResult.userName}</strong>:
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between mb-4">
              <code className="text-xl font-bold text-emerald-700">{pwdResetResult.defaultPassword}</code>
              <button onClick={() => copyToClipboard(pwdResetResult.defaultPassword)}
                className="p-2 hover:bg-emerald-100 text-emerald-600 rounded-lg transition-colors ml-3">
                <Copy size={16} />
              </button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 mb-5 space-y-1">
              <p className="font-semibold">Important:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Share this password securely with the user</li>
                <li>Advise them to change it after logging in</li>
                <li>This password will not be shown again</li>
              </ul>
            </div>
            <div className="flex justify-end gap-3">
              <Btn variant="success" onClick={() => copyToClipboard(pwdResetResult.defaultPassword)}>
                <Copy size={13} /> Copy Password
              </Btn>
              <Btn variant="outline" onClick={() => setPwdResetResult(null)}>Close</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Role management */}
      {roleModal && (
        <Modal>
          <ModalHeader icon={Crown} iconColor="#8b5cf6" title="Manage User Role" onClose={() => setRoleModal(null)} />
          <div className="px-6 py-5">
            <div className="bg-slate-50 rounded-xl p-3 mb-5">
              <p className="text-xs text-slate-500 mb-1">User</p>
              <p className="text-sm font-semibold text-slate-800">{roleModal.name || roleModal.email}</p>
              <p className="text-xs text-slate-500 mt-1.5">Current role:&nbsp;
                <Badge color={getRoleColor(roleModal)}>{getRoleDisplay(roleModal)}</Badge>
              </p>
            </div>
            <div className="space-y-2 mb-5">
              {availableRoles.map(role => {
                const current = roleModal.is_superuser ? 'superuser' : roleModal.is_staff ? 'staff' : 'regular';
                const isCurrent = current === role.key;
                return (
                  <button key={role.key} onClick={() => !isCurrent && updateRole(role.key)}
                    disabled={isCurrent || actionLoading === roleModal.id}
                    className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${
                      isCurrent ? 'border-teal-300 bg-teal-50 cursor-default' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 cursor-pointer'
                    } disabled:opacity-60`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{role.display}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{role.description}</p>
                      </div>
                      {isCurrent && <CheckCircle size={16} style={{ color: TEAL }} />}
                    </div>
                  </button>
                );
              })}
            </div>
            {actionLoading === roleModal.id && (
              <div className="flex items-center justify-center gap-2 mb-4 text-sm text-slate-500">
                <RefreshCw size={14} className="animate-spin" /> Updating role...
              </div>
            )}
            <div className="flex justify-end">
              <Btn variant="outline" onClick={() => setRoleModal(null)} disabled={actionLoading === roleModal.id}>Close</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserManagement;